import { Injectable } from '@angular/core';

import { Observable, of } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';

import {
    ApiJob,
    BaseService,
    BatchObject,
    BatchService,
    JobsDataService,
    JobStatus,
    mapToPaginatedResult,
    PaginatedBatch,
    PaginatedResults,
    PaginationInfo,
    SearchState,
    User,
    UserId,
    UserService
} from '@stylehaven/components';

@Injectable({ providedIn: 'root' })
export class PaginatedBatchSandbox extends BaseService {
    private cachedUsers: Record<string, Observable<User>> = {};

    constructor(
        private readonly userService: UserService,
        private batchService: BatchService,
        private jobService: JobsDataService
    ) {
        super();
    }

    public getBatchList(
        searchState: SearchState,
        index?: string,
        initiatorId?: string,
        status?: string
    ): Observable<PaginatedResults<PaginatedBatch>> {
        const paginationInfo: PaginationInfo = {
            page: searchState.page,
            resultsPerPage: searchState.pageSize
        };
        return this.batchService.getBatchList(searchState, index, initiatorId, status).pipe(
            map(results => ({
                ...results,
                Results: results.Results.map(batch => this.batchToPaginatedBatch(batch))
            })),
            map(results => mapToPaginatedResult(results, paginationInfo.resultsPerPage))
        );
    }

    public isBatchRunning(): Observable<boolean> {
        return this.userService.getCurrentUser().pipe(
            map(user => user.Id),
            switchMap(userId => this.batchService.getRunningBatch(userId)),
            map(response => (response.NrOfResults !== 0 ? this.batchToPaginatedBatch(response.Results[0]) : null)),
            switchMap(batch => (batch ? batch.Status$ : of(JobStatus.COMPLETED))),
            map(status => status === JobStatus.WAITING || status === JobStatus.IN_PROGRESS)
        );
    }

    private batchToPaginatedBatch(batchObject: BatchObject): PaginatedBatch {
        // These use lazy loading, so it's safe to set them on the object
        const jobs$: Observable<ApiJob[]> = this.jobService.getAllBatchJobs(batchObject.Id);
        const user$: Observable<User> = this.getAndCacheBatchUser(batchObject.UserId as UserId);

        return new PaginatedBatch(batchObject, jobs$, user$);
    }

    private getAndCacheBatchUser(userId: UserId): Observable<User> {
        if (!Object.prototype.hasOwnProperty.call(this.cachedUsers, userId)) {
            this.cachedUsers[userId] = this.userService.getUserById(userId).pipe(shareReplay(1));
        }

        return this.cachedUsers[userId];
    }

    // #endregion
}
